import { Filter, Loader2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { IntegrationsList, IntegrationsSortMenu } from "../components";
import { useAvailableModules, useIntegrations } from "../hooks";
import type { IntegrationSortBy, IntegrationSortOrder } from "../types";
import { INTEGRATION_SORT_FIELD } from "../utils";
import {
  buildActiveFilters,
  deriveIsActiveFromStatus,
  deriveSortState,
  INTEGRATION_FILTER_KEYS,
  MODULE_FILTER_KEY,
  STATUS_FILTER_KEY,
} from "../utils/integrations-list-params";

import ModernPageLayout from "@/components/layout/ModernPageLayout";
import { Button } from "@/components/ui/v2/button";
import {
  FiltersDrawer,
  type FilterSectionConfig,
} from "@/components/ui/v2/filters/FiltersDrawer";
import SearchInput from "@/components/ui/v2/search-input";
import {
  TableActiveFilters,
  type ActiveFilter,
} from "@/components/ui/v2/table/table-active-filters";
import { ROUTES } from "@/constants/routes";
import { URL_PARAMS } from "@/constants/urlParams";
import { useAuth } from "@/lib/hooks/useAuth";
import { useSearchSearchParams } from "@/lib/hooks/useSearchSearchParams";
import { useSortSearchParams } from "@/lib/hooks/useSortSearchParams";
import { clearFilterValues, removeFilterValue } from "@/lib/utils/filterParams";
import type { FilterOption } from "@/types/FilterOption";
import { UserRole } from "@/types/user/userType";

function IntegrationsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { tenantId, role, context } = useAuth();
  const { clientId } = useParams<{ clientId?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const { searchValue, debouncedSearchValue, setSearchValue } =
    useSearchSearchParams();
  const { orderBy: rawOrderBy, descending: rawDescending } =
    useSortSearchParams();
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    if (context === "admin" && !clientId && role !== UserRole.SUPER_ADMIN) {
      navigate(ROUTES.clients, { replace: true });
    }
  }, [context, clientId, role, navigate]);

  const moduleFilters = searchParams.getAll(MODULE_FILTER_KEY);
  const statusFilters = searchParams.getAll(STATUS_FILTER_KEY);
  const isActive = deriveIsActiveFromStatus(statusFilters);
  const { orderBy, descending, sortBy, order } = deriveSortState(
    rawOrderBy,
    rawDescending,
  );

  const integrationsQuery = useIntegrations({
    tenantId,
    clientId,
    isActive,
    search: debouncedSearchValue,
    hasModule: moduleFilters,
    orderBy,
    descending,
    includes: ["modules"],
  });

  const integrations = integrationsQuery.data ?? [];

  const setSort = useCallback(
    (field: string, isDescending: boolean) => {
      const next = new URLSearchParams(searchParams);
      next.set(URL_PARAMS.ORDER_BY, field);
      next.set(URL_PARAMS.DESCENDING, String(isDescending));
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );

  const handleSortByChange = (value: IntegrationSortBy) =>
    setSort(INTEGRATION_SORT_FIELD[value], descending);
  const handleOrderChange = (value: IntegrationSortOrder) =>
    setSort(orderBy, value === "desc");

  const { moduleSlugs } = useAvailableModules({ tenantId, clientId });

  const moduleOptions: FilterOption<string>[] = useMemo(
    () =>
      moduleSlugs
        .map((slug) => ({
          value: slug,
          label: t(`integrations.modules.names.${slug}`, slug),
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [moduleSlugs, t],
  );

  const statusOptions: FilterOption<string>[] = useMemo(
    () => [
      { value: "active", label: t("common.active") },
      { value: "inactive", label: t("common.inactive") },
    ],
    [t],
  );

  const filterSections: FilterSectionConfig[] = useMemo(
    () => [
      { key: STATUS_FILTER_KEY, title: "common.status", options: statusOptions },
      { key: MODULE_FILTER_KEY, title: "common.modules", options: moduleOptions },
    ],
    [statusOptions, moduleOptions],
  );

  const moduleLabelByValue = useMemo(
    () => new Map(moduleOptions.map((o) => [o.value, o.label])),
    [moduleOptions],
  );
  const statusLabelByValue = useMemo(
    () => new Map(statusOptions.map((o) => [o.value, o.label])),
    [statusOptions],
  );

  const activeFilters: ActiveFilter[] = useMemo(
    () =>
      buildActiveFilters(statusFilters, moduleFilters, {
        statusLabel: t("common.status"),
        moduleLabel: t("common.modules"),
        statusValueLabel: (value) => statusLabelByValue.get(value),
        moduleValueLabel: (value) => moduleLabelByValue.get(value),
      }),
    [moduleFilters, statusFilters, moduleLabelByValue, statusLabelByValue, t],
  );

  const removeFilter = useCallback(
    (filter: ActiveFilter) => {
      setSearchParams(
        removeFilterValue(searchParams, filter.key, String(filter.value)),
      );
    },
    [searchParams, setSearchParams],
  );

  const clearFilters = useCallback(() => {
    setSearchParams(clearFilterValues(searchParams, INTEGRATION_FILTER_KEYS));
  }, [searchParams, setSearchParams]);

  const handleManage = (integrationName: string) => {
    if (clientId) {
      navigate(
        `/${ROUTES.clients}/${clientId}/${ROUTES.integrations}/${integrationName}`,
      );
      return;
    }
    navigate(`/${ROUTES.integrations}/${integrationName}`);
  };

  const renderContent = () => {
    if (integrationsQuery.isPending) {
      return (
        <div className="flex h-full w-full items-center justify-center p-6">
          <Loader2 className="size-16 animate-spin text-muted-foreground" />
        </div>
      );
    }

    if (integrationsQuery.isError) {
      return (
        <div className="rounded-md border border-dashed border-border px-4 py-10 text-center text-sm text-muted-foreground">
          {t("integrations.failedToLoad")}
        </div>
      );
    }

    if (integrations.length === 0) {
      const hasFilters =
        !!debouncedSearchValue ||
        moduleFilters.length > 0 ||
        statusFilters.length > 0;
      return (
        <div className="rounded-md border border-dashed border-border px-4 py-10 text-center text-sm text-muted-foreground">
          <p>
            {t(
              hasFilters
                ? "integrations.noResultsFound"
                : "integrations.noIntegrationsAvailable",
            )}
          </p>
        </div>
      );
    }

    return (
      <IntegrationsList
        integrations={integrations}
        onManage={handleManage}
      />
    );
  };

  return (
    <ModernPageLayout
      title={t("common.integrations")}
      subtitle={t("integrations.subHeader")}
    >
      <div className="w-full flex flex-wrap items-center gap-4 mb-4">
        <SearchInput
          className="w-full max-w-lg"
          placeholder={t("integrations.searchPlaceholder")}
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
        />
        <IntegrationsSortMenu
          sortBy={sortBy}
          order={order}
          onSortByChange={handleSortByChange}
          onOrderChange={handleOrderChange}
        />
        <div className="flex items-center gap-3 sm:ml-auto">
          <Button
            variant="outline"
            type="button"
            onClick={() => setFiltersOpen(true)}
          >
            <Filter className="h-4 w-4" />
            {t("common.filters")}
          </Button>
        </div>
      </div>

      {activeFilters.length > 0 && (
        <div className="mb-4">
          <TableActiveFilters
            filters={activeFilters}
            onRemoveFilter={removeFilter}
            onClearFilters={clearFilters}
          />
        </div>
      )}

      {renderContent()}

      <FiltersDrawer
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        searchParams={searchParams}
        filterKeys={INTEGRATION_FILTER_KEYS}
        filterSections={filterSections}
        entityName={t("common.integrations")}
        defaultExpanded
      />
    </ModernPageLayout>
  );
}

export default IntegrationsPage;
